<p-dialog [(visible)]="redeemDialogVisible" [closable]="false" [focusOnShow]="false" [draggable]="false" [modal]="true"
  [resizable]="false"
  styleClass="z-[1000] py-0 rounded-3xl w-auto border-0 shadow-2xl shadow-amber-500/30 focus:outline-none backdrop-blur-xl">
  
  <!-- Main Container with Gradient Background -->
  <div class="relative mx-auto my-8 w-full max-w-4xl overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95 backdrop-blur-xl border border-amber-200/20 shadow-2xl">
    
    <!-- Decorative Elements -->
    <div class="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-blue-500/5"></div>
    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl opacity-20"></div>
    
    <!-- Content Container -->
    <div class="relative z-10 p-8">
      
      <!-- Header Section -->
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/25 mb-4">
          <svg class="w-8 h-8 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-400 mb-2">
          Access Required
        </h2>
      </div>

      <!-- Beta Notice -->
      <div class="bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-xl p-6 mb-6 border border-amber-200/20">
        <p class="text-center text-amber-100/90 text-lg leading-relaxed font-medium">
          Clinical AI Studio is currently in <span class="text-amber-300 font-semibold">closed beta</span>. 
          If you'd like access, kindly email us at
          <a href="mailto:info&#64;ailens.ai" 
             class="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 transition-all duration-300 underline decoration-amber-300/50 hover:decoration-amber-200 underline-offset-2 font-semibold">
            info&#64;ailens.ai
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
            </svg>
          </a>
        </p>
      </div>

      <!-- Access Code Form -->
      <form class="space-y-6" (submit)="onAccessCodeSubmit($event, accCode.value)">
        <div class="space-y-3">
          <label for="access-code" class="block text-sm font-semibold text-amber-200 mb-2">
            Enter Access Code
          </label>
          
          <div class="relative group">
            <div class="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-blue-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <input #accCode 
                   id="access-code" 
                   name="access_code" 
                   type="text" 
                   spellcheck="false" 
                   placeholder="Enter your access code..." 
                   class="relative w-full px-4 py-3 bg-slate-800/50 border border-amber-200/30 rounded-xl text-amber-100 placeholder:text-amber-200/50 focus:outline-none focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20 focus:bg-slate-800/80 transition-all duration-300 text-center text-lg font-medium backdrop-blur-sm"
                   aria-label="Access code" 
                   [disabled]="isSubmittingAccessCode()">
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-center">
          <button type="submit"
                  class="group relative px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 rounded-xl font-semibold text-base shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-amber-500/25 transition-all duration-300 transform hover:scale-105 active:scale-95 min-w-[180px] flex items-center justify-center gap-3"
                  [disabled]="isSubmittingAccessCode()">
            
            <!-- Loading Spinner -->
            @if(isSubmittingAccessCode()) {
              <div class="flex items-center gap-3">
                <svg class="animate-spin h-5 w-5 text-slate-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Verifying...</span>
              </div>
            } @else {
              <div class="flex items-center gap-3">
                <svg class="w-5 h-5 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                </svg>
                <span>Redeem Code</span>
              </div>
            }
            
            <!-- Button Shine Effect -->
            <!-- <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div> -->
          </button>
        </div>
      </form>
      
      <!-- Footer Help Text -->
      <div class="mt-8 pt-6 border-t border-amber-200/10">
        <p class="text-center text-amber-200/70 text-sm">
          Need help? Contact our support team for assistance.
        </p>
      </div>
    </div>
  </div>
</p-dialog>